import { SKYFISH } from "../constants";
import type { Vec2 } from "../math";

/**
 * A school of skyfish circling a rock (see SKYFISH): all of it is these few
 * numbers, and where each fish is follows from them (see fishOf), so the
 * server and everyone drawing it agree on every fish.
 */
export interface School {
  /** How far round the rock it's got (radians, not wrapped), and which way it goes (1 anticlockwise, -1 clockwise). */
  round: number;
  turn: number;
  /** How far out from the rock's middle it swims. */
  out: number;
  /** How many fish it started with, and which are still alive (bit i, fish i). */
  n: number;
  alive: number;
  /** What makes each school's fish its own (their spacing, wobble, size, colour). */
  seed: number;
  /** How scattered it is, 0 calm to 1 fleeing (see SKYFISH.scatter). */
  scatter: number;
}

/** A number 0..1 from a school's seed and `salt`. */
export function schoolHash(seed: number, salt: number): number {
  let h = Math.imul(seed ^ Math.imul(salt + 1, 374761393), 668265263);
  h ^= h >>> 13;
  h = Math.imul(h, 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** One fish of a school: where it is from the rock's middle, which way it's heading (radians), and how big it is (1 the usual). */
export interface Fish {
  at: Vec2;
  heading: number;
  size: number;
}

/**
 * Where fish `i` of school `s` is. Strung out along the way round, each a
 * little in or out and weaving; scattering, they spread apart and out.
 */
export function fishOf(s: School, i: number): Fish {
  const along = (i - (s.n - 1) / 2) * (SKYFISH.spacing / Math.max(s.out, 4));
  const phase = schoolHash(s.seed, 10 + i) * Math.PI * 2;
  const a = s.round + along * (1 + s.scatter * 1.5) + Math.sin(s.round * 5 + phase) * 0.02;
  const r = s.out + (schoolHash(s.seed, 30 + i) - 0.5) * 4 * (1 + s.scatter * 2) + Math.sin(s.round * 3 + phase) * 1.2 + s.scatter * (4 + i * 1.5);
  return {
    at: { x: Math.cos(a) * r, y: Math.sin(a) * r },
    heading: a + (s.turn * Math.PI) / 2 + Math.sin(s.round * 4 + phase) * 0.1,
    size: 0.85 + schoolHash(s.seed, 50 + i) * 0.35,
  };
}

/** Is fish `i` of school `s` alive? */
export const fishAlive = (s: School, i: number): boolean => (s.alive & (1 << i)) !== 0;
